import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { gsap, ScrollTrigger, MOTION_OK } from "./motion";

const SmoothScrollContext = createContext<Lenis | null>(null);

export function SmoothScrollProvider({ children }: { children: ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null);

  useEffect(() => {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
    if (!window.matchMedia(MOTION_OK).matches) return;

    const instance = new Lenis({ autoRaf: false, lerp: 0.11, wheelMultiplier: 0.9 });
    instance.on("scroll", ScrollTrigger.update);
    const raf = (time: number) => instance.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);
    setLenis(instance);

    // Re-measure the scroll limit whenever layout changes (rotation, fonts, split text).
    const resize = () => instance.resize();
    window.addEventListener("resize", resize);
    ScrollTrigger.addEventListener("refresh", resize);

    return () => {
      window.removeEventListener("resize", resize);
      ScrollTrigger.removeEventListener("refresh", resize);
      gsap.ticker.remove(raf);
      instance.destroy();
      setLenis(null);
    };
  }, []);

  return <SmoothScrollContext.Provider value={lenis}>{children}</SmoothScrollContext.Provider>;
}

/** Scrolls to a hash target, through Lenis when it is running. */
export function useScrollTo() {
  const lenis = useContext(SmoothScrollContext);
  return (hash: string) => {
    const target = document.querySelector<HTMLElement>(hash);
    if (!target) return;
    if (lenis) lenis.scrollTo(target, { offset: -64, duration: 1.2 });
    else target.scrollIntoView({ block: "start" });
    target.focus({ preventScroll: true });
  };
}
